import React, { useEffect, useState } from 'react'

import { Button, Icon } from '@/shared/components'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowRight, faRocket } from '@fortawesome/pro-regular-svg-icons'
import { alpha, Box, keyframes, styled, Typography } from '@mui/material'

export type WelcomeOverlayProps = {
  show: boolean
  onFinish: () => void
  onUpgrade: () => void
}


export const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({
  show,
  onFinish,
  onUpgrade,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const { subscription } = useSubscription()

  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [show])

  // Check if user is on free tier
  const isFreeTier = !subscription || subscription.tierName === 'Free'

  if (!show) return null

  return (
    <OverlayContainer $isVisible={isVisible}>
      <ContentBox>
        <WelcomeAnimation $isVisible={isVisible}>
          <Logo src="/login-logo-dark.svg" alt="FastMile" />

          <WelcomeTitle variant="h1">
            Welcome to FastMile
          </WelcomeTitle>

          <WelcomeMessage variant="body1">
            Your account has been successfully set up and is ready to use.
            {isFreeTier && ' You\'re currently on the Free plan with access to basic features.'}
          </WelcomeMessage>

          <ActionContainer>
            {isFreeTier && (
              <ActionButton
                primary
                endIcon={<Icon icon={faRocket} />}
                onClick={onUpgrade}
                size="large"
              >
                Upgrade for More Features
              </ActionButton>
            )}

            <ActionButton
              outlined
              endIcon={<Icon icon={faArrowRight} />}
              onClick={onFinish}
            >
              Start Planning
            </ActionButton>
          </ActionContainer>
        </WelcomeAnimation>
      </ContentBox>
    </OverlayContainer>
  )
}

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Styled components
const OverlayContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isVisible',
}) <{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => alpha(props.theme.palette.background.default, 0.9)};
  backdrop-filter: blur(8px);
  z-index: ${props => props.theme.zIndex.modal + 2};
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.8s ease-in-out;
`

const ContentBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: ${pxToRem(600)};
  padding: ${pxToRem(32)};
  text-align: center;
`

const WelcomeAnimation = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isVisible',
}) <{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${props => props.$isVisible ? fadeIn : 'none'} 1s ease-out;
  opacity: 1;
  transition: opacity 0.8s ease-in-out;
`

const Logo = styled('img')`
  height: ${pxToRem(120)};
  margin-bottom: ${pxToRem(32)};
  animation: ${slideUp} 1s ease-out 0.3s both;
`

const WelcomeTitle = styled(Typography)`
  font-size: ${pxToRem(36)};
  font-weight: 600;
  margin-bottom: ${pxToRem(16)};
  color: ${props => props.theme.palette.primary.main};
  animation: ${slideUp} 1s ease-out 0.5s both;
`

const WelcomeMessage = styled(Typography)`
  font-size: ${pxToRem(18)};
  line-height: 1.6;
  color: ${props => props.theme.palette.text.secondary};
  margin-bottom: ${pxToRem(32)};
  max-width: ${pxToRem(480)};
  animation: ${slideUp} 1s ease-out 0.7s both;
`

const ActionContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  width: 100%;

  ${props => props.theme.breakpoints.up('sm')} {
    gap: ${pxToRem(16)};
  }
`

const ActionButton = styled(Button)`
  animation: ${slideUp} 1s ease-out 0.9s both;
  padding: ${pxToRem(12)} ${pxToRem(24)};
  font-size: ${pxToRem(16)};
  font-weight: 500;
  flex: 1;

  .MuiSvgIcon-root {
    transition: transform 0.2s ease-in-out;
  }

  &:hover {
    .MuiSvgIcon-root {
      transform: translateX(3px);
    }
  }
`
